import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { httpGet, httpPost } from "../../services/httpService";
import {
  getPatientPrescriptionsUrl,
  updatePatientPrescriptionUrl,
  verifyPinForPrescriptionApprovalUrl,
  patientPrescriptionBulkUpdateUrl,
  getPatientPrescriptionByIdUrl,
  getPrescriptionByPrescriptionIDUrl,
  declinePrescriptionByIdUrl,
  generateVaccinePdfUrl
} from "../../apiUrls/apiUrls";
import { logErrorWithPayload } from "../../services/logService";

const initialState = {
  isPatientPrescriptionsLoading: false,
  patientPrescriptions: [],
  isUpdatePatientPrescriptionLoading: false,
  UpdatePatientPrescription: "",
  isVerifyPinForPrescriptionApprovalLoading: false,
  userVerification: "",
  isPatientPrescriptionBulkUpdateLoading: false,
  PatientPrescriptionBulkUpdate: "",
  isPatientPrescriptionByIdLoading: false,
  PatientPrescriptionById: null,
  prescriptionImagePreview: null,
  previewPrescription: false,
  prescriptionImage: "",
  prescriptionImageLoader: false,
  showPrintPreviewLoading: false,
  declinePrescriptionLoading: false,
  consultationPrescriptionImagePreview: null,
  vaccinePdfDownloadPayload: null,
};

export const getPatientPrescriptionsData = createAsyncThunk(
  "patientPrescriptions/getPatientPrescriptionsData",
  async (companyID) => {
    const urlToGet = getPatientPrescriptionsUrl(companyID);
    const respData = await httpGet(urlToGet);
    return respData;
  }
);

export const updatePatientPrescriptionData = createAsyncThunk(
  "patientPrescriptions/updatePatientPrescriptionData",
  async (params, thunkAPI) => {
    const urlToGet = updatePatientPrescriptionUrl();
    const respData = await httpPost(urlToGet, params);
    return respData;
  }
);

export const verifyPinForPrescriptionApprovalData = createAsyncThunk(
  "patientPrescriptions/verifyPinForPrescriptionApprovalData",
  async (pinCode) => {
    const urlToGet = verifyPinForPrescriptionApprovalUrl(pinCode);
    const respData = await httpGet(urlToGet);
    return respData;
  }
);

export const patientPrescriptionBulkUpdateData = createAsyncThunk(
  "patientPrescriptions/patientPrescriptionBulkUpdateData",
  async (params, thunkAPI) => {
    const urlToGet = patientPrescriptionBulkUpdateUrl();
    const respData = await httpPost(urlToGet, params);
    return respData;
  }
);

export const patientPrescriptionDownloadPdfForVaccineData = createAsyncThunk(
  "patientPrescriptions/patientPrescriptionDownloadPdfForVaccineData",
  async (params, thunkAPI) => {
    const urlToGet = generateVaccinePdfUrl();
    const respData = await httpPost(urlToGet, params);
    return respData;
  }
);

export const getPatientPrescriptionByIdData = createAsyncThunk(
  "patientPrescriptions/getPatientPrescriptionByIdData",
  async (params) => {
    const urlToGet = getPatientPrescriptionByIdUrl(params);
    const respData = await httpGet(urlToGet);
    return respData;
  }
);
export const getPrescriptionByPrescriptionIdData = createAsyncThunk(
  "patientPrescriptions/getPrescriptionByPrescriptionIdData",
  async (params) => {
    const urlToGet = getPrescriptionByPrescriptionIDUrl(params);
    const respData = await httpGet(urlToGet);
    return respData;
  }
);
export const declinePrescriptionByIdData = createAsyncThunk(
  "patientPrescriptions/declinePrescriptionByIdData",
  async (params, thunkAPI) => {
    const urlToGet = declinePrescriptionByIdUrl();
    const respData = await httpPost(urlToGet, params);
    return respData;
  }
);
const patientPrescriptionSlice = createSlice({
  name: "patientPrescription",
  initialState,
  reducers: {
    setIsPatientPrescriptionsLoading: (state, action) => {
      state.isPatientPrescriptionsLoading = action?.payload || false;
    },
    setPrescriptionImagePreviewData: (state, action) => {
      state.prescriptionImagePreview = action?.payload || null;
    },
    setConsultationPrescriptionImagePreviewData: (state, action) => {
      state.consultationPrescriptionImagePreview = action?.payload || null;
    },
    setPreviewPrescriptionData: (state, action) => {
      state.previewPrescription = action?.payload || false;
    },
    setShowPrintPreviewLoadingData: (state, action) => {
      state.showPrintPreviewLoading = action?.payload || false;
    },
    setVaccinePdfDownloadPayload: (state, action) => {
      state.vaccinePdfDownloadPayload = action?.payload || null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPatientPrescriptionsData.pending, (state) => {
        state.isPatientPrescriptionsLoading = true;
        state.patientPrescriptions = [];
      })
      .addCase(getPatientPrescriptionsData.fulfilled, (state, action) => {
        state.isPatientPrescriptionsLoading = false;
        state.patientPrescriptions = action?.payload;
      })
      .addCase(getPatientPrescriptionsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isPatientPrescriptionsLoading = false;
        state.patientPrescriptions = [];
      })
      .addCase(updatePatientPrescriptionData.pending, (state) => {
        state.isUpdatePatientPrescriptionLoading = true;
      })
      .addCase(updatePatientPrescriptionData.fulfilled, (state, action) => {
        state.isUpdatePatientPrescriptionLoading = false;
        state.UpdatePatientPrescription = action?.payload;
      })
      .addCase(updatePatientPrescriptionData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isUpdatePatientPrescriptionLoading = false;
      })
      .addCase(verifyPinForPrescriptionApprovalData.pending, (state) => {
        state.isVerifyPinForPrescriptionApprovalLoading = true;
      })
      .addCase(
        verifyPinForPrescriptionApprovalData.fulfilled,
        (state, action) => {
          state.isVerifyPinForPrescriptionApprovalLoading = false;
          state.userVerification = action?.payload;
        }
      )
      .addCase(
        verifyPinForPrescriptionApprovalData.rejected,
        (state, action) => {
          logErrorWithPayload(action?.error);
          state.isVerifyPinForPrescriptionApprovalLoading = false;
        }
      )
      .addCase(patientPrescriptionBulkUpdateData.pending, (state) => {
        state.isPatientPrescriptionBulkUpdateLoading = true;
      })
      .addCase(patientPrescriptionBulkUpdateData.fulfilled, (state, action) => {
        state.isPatientPrescriptionBulkUpdateLoading = false;
        state.PatientPrescriptionBulkUpdate = action?.payload;
      })
      .addCase(patientPrescriptionBulkUpdateData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isPatientPrescriptionBulkUpdateLoading = false;
      })

      .addCase(getPatientPrescriptionByIdData.pending, (state) => {
        state.isPatientPrescriptionByIdLoading = true;
        state.PatientPrescriptionById = null;
      })
      .addCase(getPatientPrescriptionByIdData.fulfilled, (state, action) => {
        state.isPatientPrescriptionByIdLoading = false;
        state.PatientPrescriptionById = action?.payload;
      })
      .addCase(getPatientPrescriptionByIdData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isPatientPrescriptionByIdLoading = false;
        state.PatientPrescriptionById = null;
      })
      .addCase(patientPrescriptionDownloadPdfForVaccineData.pending, (state) => {
      })
      .addCase(patientPrescriptionDownloadPdfForVaccineData.fulfilled, (state, action) => {
      })
      .addCase(patientPrescriptionDownloadPdfForVaccineData.rejected, (state, action) => {
      })
      .addCase(getPrescriptionByPrescriptionIdData.pending, (state) => {
        state.prescriptionImageLoader = true;
      })
      .addCase(getPrescriptionByPrescriptionIdData.fulfilled, (state, action) => {
        state.prescriptionImageLoader = false;
        state.prescriptionImage = action?.payload;
      })
      .addCase(getPrescriptionByPrescriptionIdData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.prescriptionImageLoader = false;
        state.prescriptionImage = "";
      })
      .addCase(declinePrescriptionByIdData.pending, (state) => {
        state.declinePrescriptionLoading = true;
      })
      .addCase(declinePrescriptionByIdData.fulfilled, (state, action) => {
        state.declinePrescriptionLoading = false;
      })
      .addCase(declinePrescriptionByIdData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.declinePrescriptionLoading = false;
      });
  },
});

export const { setIsPatientPrescriptionsLoading, setPrescriptionImagePreviewData, setPreviewPrescriptionData, setShowPrintPreviewLoadingData,setConsultationPrescriptionImagePreviewData,setVaccinePdfDownloadPayload,vaccinePdfDownloadPayload } =
  patientPrescriptionSlice.actions;

export default patientPrescriptionSlice.reducer;
